import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { ARKIT_NAMES } from '../network/AudioAnimationBuffer.js'

/**
 * Diagnose morph targets in a GLB file.
 * Shows which ARKit shapes are present and which are missing.
 */
export async function diagnosticMorphTargets(glbPath) {
  return new Promise((resolve, reject) => {
    new GLTFLoader().load(
      glbPath,
      (gltf) => {
        try {
          const result = {
            path: glbPath,
            meshes: [],
            allMorphNames: new Set(),
            arkitMatches: [],
            arkitMissing: [],
            totalMorphTargets: 0,
          }

          // Traverse and collect all meshes with morph targets
          gltf.scene.traverse((node) => {
            if (node.isMesh && node.morphTargetInfluences && node.morphTargetInfluences.length > 0) {
              const morphNames = node.morphTargetDictionary ? Object.keys(node.morphTargetDictionary) : []

              result.meshes.push({
                name: node.name,
                morphCount: morphNames.length,
                morphNames: morphNames,
              })

              morphNames.forEach(name => result.allMorphNames.add(name))
              result.totalMorphTargets += morphNames.length
            }
          })

          // Check which ARKit names are present
          ARKIT_NAMES.forEach(arkitName => {
            const found = Array.from(result.allMorphNames).find(name =>
              name.toLowerCase().includes(arkitName.toLowerCase()) ||
              arkitName.toLowerCase().includes(name.toLowerCase())
            )

            if (found) {
              result.arkitMatches.push({ arkit: arkitName, found: found })
            } else {
              result.arkitMissing.push(arkitName)
            }
          })

          // Log results
          console.log('\n========== MORPH TARGET DIAGNOSTIC ==========')
          console.log(`File: ${glbPath}`)
          console.log(`Total meshes with morphs: ${result.meshes.length}`)
          console.log(`Total unique morph targets: ${result.allMorphNames.size}`)
          console.log(`Total morph target slots: ${result.totalMorphTargets}`)
          console.log('')

          console.log('--- MESH DETAILS ---')
          result.meshes.forEach((mesh, idx) => {
            console.log(`[${idx}] ${mesh.name}: ${mesh.morphCount} morph targets`)
          })
          console.log('')

          console.log(`--- ARKit COVERAGE ---`)
          console.log(`Matched: ${result.arkitMatches.length} / ${ARKIT_NAMES.length}`)
          console.log(`Missing: ${result.arkitMissing.length} / ${ARKIT_NAMES.length}`)
          console.log('')

          if (result.arkitMatches.length > 0) {
            console.log('✓ MATCHED ARKit SHAPES:')
            result.arkitMatches.forEach(({ arkit, found }) => {
              console.log(`  ${arkit} → ${found}`)
            })
            console.log('')
          }

          if (result.arkitMissing.length > 0) {
            console.log('✗ MISSING ARKit SHAPES:')
            result.arkitMissing.forEach(arkit => {
              console.log(`  ${arkit}`)
            })
            console.log('')
          }

          console.log('--- ALL AVAILABLE MORPH TARGETS ---')
          const allNames = Array.from(result.allMorphNames).sort()
          allNames.forEach((name, idx) => {
            const isArkit = result.arkitMatches.find(m => m.found === name)
            const status = isArkit ? '✓' : ' '
            console.log(`[${status}] ${idx + 1}. ${name}`)
          })
          console.log('==========================================\n')

          resolve(result)
        } catch (err) {
          console.error('[Diagnostic] Error analyzing GLB:', err)
          reject(err)
        }
      },
      (progress) => {
        const pct = (progress.loaded / progress.total * 100).toFixed(1)
        console.log(`[Diagnostic] Loading ${glbPath}: ${pct}%`)
      },
      (err) => {
        console.error('[Diagnostic] Failed to load GLB:', err)
        reject(err)
      }
    )
  })
}

// Expose globally for console debugging
window._diagnosticMorphs = async (path = '/assets/Myra_MorphRig_v001.glb') => {
  console.log(`[Diagnostic] Starting morph target analysis of ${path}...`)
  return await diagnosticMorphTargets(path)
}
